import { Cog, Plug } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountMcpConnectButton } from '@/Domain/components/Integration/AccountMcpConnect/AccountMcpConnectButton';
import { McpForm } from '@/Domain/components/Integration/McpIntegration/McpForm/McpForm';
import { McpConnectInstructions } from '@/Domain/components/Integration/McpIntegration/McpIntegration/McpIntegration';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { useMcpForm } from '@/Domain/hooks/Action/useMcpForm/useMcpForm';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const McpIntegrationSheet = () => {
  const { t } = useTranslation('mcp_page');
  const { isOpen, closeModal } = useModalStore();
  const { data, handleMutation } = useMcpForm();
  const [configureOpen, setConfigureOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  return (
    <Sheet open={isOpen(MODAL_NAMES.MCP_INTEGRATION_SHEET)} onOpenChange={closeModal}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        tabIndex={undefined}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          closeModal();
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <McpIcon className="size-8 shrink-0 text-red-500" />
            <div className="flex flex-col gap-1">
              <SheetTitle>{t('title')}</SheetTitle>
            </div>
          </div>
          <IconButton onClick={() => closeModal()} variant="secondary" className="shrink-0">
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
          <IntegrationIntroduction
            description={t('overview')}
            prerequisites={t('prerequisites', { returnObjects: true }) as string[]}
            docsHref="https://docs.quickchat.ai/channels/mcp"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="mcp-connect-enable-section"
          >
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => setConfigureOpen(true)}
                data-testid="mcp-configure-button"
              >
                <Cog className="size-4" />
                {t('configure_button')}
              </Button>
              <Button
                type="button"
                variant="dark"
                size="sm"
                className="w-full"
                onClick={() => setConnectOpen(true)}
                data-testid="mcp-connect-button"
              >
                <Plug className="size-4" />
                {t('connect_button')}
              </Button>
            </div>
          </IntegrationSection>

          <IntegrationSection
            title={t('integrations:section.settings')}
            data-testid="mcp-settings-section"
          >
            <IntegrationToggleField
              id="mcp-active-switch"
              label={t('integrations:enable', { name: 'MCP' })}
              checked={data?.active ?? false}
              onCheckedChange={(checked) => handleMutation({ active: checked })}
              data-testid="mcp-active-switch"
            />

            <IntegrationToggleField
              id="mcp-visibility-switch"
              label={t('form.visibility_label')}
              tooltip={t('form.visibility_sublabel')}
              checked={(data?.visibility ?? 'private') === 'private'}
              onCheckedChange={(checked) =>
                handleMutation({ visibility: checked ? 'private' : 'public' })
              }
              data-testid="mcp-visibility-switch"
            />
          </IntegrationSection>

          <div
            className="rounded-lg border border-border bg-muted/50 p-4 space-y-2.5"
            data-testid="mcp-account-cross-link"
          >
            <p className="text-sm text-muted-foreground">{t('manage_instead')}</p>
            <AccountMcpConnectButton source="mcp_channel_sheet" className="w-full" />
          </div>
        </div>

        <IntegrationConnectDialog
          open={configureOpen}
          onOpenChange={setConfigureOpen}
          title={t('configure_button')}
          description={t('configure_description')}
          submitLabel={t('integrations:done')}
          data-testid="mcp-configure-dialog"
        >
          <McpForm />
        </IntegrationConnectDialog>

        <IntegrationConnectDialog
          open={connectOpen}
          onOpenChange={setConnectOpen}
          title={t('connect_button')}
          description={t('connect_description')}
          submitLabel={t('integrations:done')}
          data-testid="mcp-connect-dialog"
        >
          <McpConnectInstructions />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
